import React, { useState, useMemo } from 'react';
import { track } from '../../services/analytics';
import { Discount, BaseGym, Product } from '../../types/Gym';
import { FormattedPrice } from '../FormattedPrice/FormattedPrice';
import { ProductDiscountMessage } from './ProductDiscountMessage';
import { ProductItem } from './ProductItem';
import { Modal } from '../Modal/Modal';
import { Button, LinkButton, Row } from '@husslehq/ui-library';

import styles from '../../components/PassPicker/Product.module.scss';

type FakeProductItemProps = {
  type: string;
  id: string;
  gym: BaseGym;
  price: number;
  originalPrice?: number;
  discount?: Discount;
  bullets: Array<{ item: string }>;
  recommended?: boolean;
  monthlyProduct?: Product | undefined;
  dailyProduct?: Product | undefined;
  isTemporarilyClosed?: boolean;
  purchasable: boolean;
};

const DirectModalContent: React.FC<Omit<FakeProductItemProps, 'type' | 'id' | 'price' | 'bullets'>> = ({
  gym,
  monthlyProduct,
  dailyProduct,
  purchasable,
}) => {
  const monthlyBullets = [
    { item: 'Multi-gym access' },
    { item: 'Unlimited visits' },
    { item: 'No contracts, cancel anytime' },
  ];
  const dailyBullets = [
    { item: 'One visit' },
    { item: 'Valid for 30 days' },
    { item: 'Buy as a one off or as often as you like' },
  ];

  return (
    <>
      <p className={styles['product__modal-sub-title']}>Gym Membership is currently unavailable</p>
      <p className={styles['product__modal-desc']}>
        We are working hard to add Gym Membership to more venues.
        <br /> In the meantime, you can access this club now with our alternative passes below.
      </p>
      <Row className={styles['product__picker-row']}>
        {monthlyProduct && (
          <ProductItem
            key={`product_${monthlyProduct && monthlyProduct.id}`}
            type={monthlyProduct.type}
            price={monthlyProduct.price}
            discount={monthlyProduct.discount}
            id={monthlyProduct.id}
            gym={gym}
            bullets={monthlyBullets}
            purchasable={purchasable}
            hideIncludedGymsLink={true}
            className="product__item--in-modal"
            sourceSegments={monthlyProduct.customer_source_segments}
          />
        )}
        {dailyProduct && (
          <ProductItem
            key={`product_${dailyProduct && dailyProduct.id}`}
            type={dailyProduct.type}
            price={dailyProduct.price}
            discount={dailyProduct.discount}
            purchasable={purchasable}
            id={dailyProduct.id}
            gym={gym}
            bullets={dailyBullets}
            className="product__item--in-modal"
            sourceSegments={dailyProduct.customer_source_segments}
          />
        )}
      </Row>
    </>
  );
};

const DefaultModalContent: React.FC<Omit<FakeProductItemProps, 'type' | 'id' | 'price' | 'bullets'>> = ({ gym }) => {
  const viewGyms = () => {
    track({
      event: 'gym_spa',
      category: 'Fake Door',
      action: `view gyms nearby`,
      label: `${gym.name}`,
    });
  };

  return (
    <>
      <p className={styles['product__modal-sub-title']}>This pass is currently unavailable for this club.</p>
      <p className={styles['product__modal-desc']}>
        We are working hard to add more venues and passes to our network and your interest has been captured. In the
        meantime, why not check out some of the other great gyms on Hussle.
      </p>
      <LinkButton size="medium" level="primary" href={`/search?location=${gym.location.postcode}`} onClick={viewGyms}>
        View available gyms nearby
      </LinkButton>
    </>
  );
};

export const FakeProductItem: React.FC<FakeProductItemProps> = ({
  type,
  id,
  gym,
  price,
  bullets,
  recommended,
  discount,
  originalPrice,
  monthlyProduct,
  dailyProduct,
  isTemporarilyClosed,
  purchasable,
}) => {
  const [showModal, setShowModal] = useState(false);
  const recommendedList = (items: any[]) => (
    <ul className={styles['product__list']}>
      {items.map((item, index) => (
        <li key={index}>
          <span className={styles['product__list-item']}>{item.item}</span>
        </li>
      ))}
    </ul>
  );

  const buyProduct = () => {
    setShowModal(true);
    track({
      event: 'gym_spa',
      category: 'Fake Door',
      action: `${fullProductName()}`,
      label: `${gym.name}`,
    });
    track({
      event: 'gym_spa',
      category: 'Fake Door',
      action: `${fullProductName()}`,
      label: `${fullProductName()}`,
    });
    track({
      event: 'gym_spa',
      category: 'Pass Picker',
      action: `Buy ${fullProductName()}`,
      label: `${gym.name}`,
    });
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const fullProductName = () => {
    switch (type) {
      case 'monthly':
        return 'Monthly+';
      case 'direct':
        return 'Gym Membership';
      case 'daypass':
        return 'Day Pass';
    }
  };

  const hasPriceCut = () => {
    const hasPriceCut = discount || (originalPrice && originalPrice > price) ? true : false;
    return hasPriceCut;
  };

  const keyPoints = () => {
    switch (type) {
      case 'monthly':
        return 'Monthly, cancel anytime.';
      case 'direct':
        return `Monthly, cancel anytime. ${`${
          originalPrice && originalPrice > price
            ? `£${originalPrice && (originalPrice / 100).toFixed(2)} after first month.`
            : ''
        }`}`;
    }
  };

  const showProductsInModal = useMemo(
    () => monthlyProduct && type === 'direct' && !isTemporarilyClosed,
    [monthlyProduct, type, isTemporarilyClosed],
  );

  return (
    <div className={styles.product__item}>
      <div
        data-testid={`product-${type}-fake`}
        className={`${styles.product} ${recommended || hasPriceCut() ? styles['product--has-label'] : ''}
          ${styles[`product--${type}`]}`}
      >
        {recommended ? (
          <p className={styles.product__label}>Recommended</p>
        ) : (
          (discount || (originalPrice && originalPrice > price)) && <p className={styles.product__label}>Offer</p>
        )}
        <div className={styles.product__upper}>
          <h3 className={styles.product__name}>{fullProductName()}</h3>
          {recommendedList(bullets)}
          {type === 'monthly' && (
            <a href={`/search/extra-gym?id=${gym.id}&pid=${id}`} className={styles['product__link']}>
              See gyms included
            </a>
          )}
        </div>
        <div className={styles.product__info}>
          {type === 'monthly' && discount && discount.discounted_price > 0 && !discount.trial_days ? (
            <FormattedPrice
              className={styles.product__price}
              classNamePounds={styles['product__price-pounds']}
              price={discount.discounted_price}
              originalPrice={price}
            />
          ) : type === 'direct' && originalPrice && originalPrice > price ? (
            <FormattedPrice
              className={styles.product__price}
              classNamePounds={styles['product__price-pounds']}
              price={price}
              originalPrice={originalPrice}
            />
          ) : (
            <FormattedPrice
              className={styles.product__price}
              classNamePounds={styles['product__price-pounds']}
              price={price}
            />
          )}
          {type !== 'daily' && <p className={styles['product__key-points']}>{keyPoints()}</p>}
        </div>
        <Button level="primary" fullWidth className={styles['product__buy-button']} onClick={buyProduct}>
          {discount ? (
            <ProductDiscountMessage discount={discount} productType={type} />
          ) : (
            <>Choose {type !== 'direct' ? fullProductName() : 'Membership'}</>
          )}
        </Button>
        <Modal onRequestClose={closeModal} visible={showModal} modalClassName="ReactModal__Content--noPadding">
          <div className={styles.product__modal}>
            <Button level="link" className={styles['product__modal-close']} onClick={closeModal}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                width="24px"
              >
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </Button>
            <h4 className={styles['product__modal-title']}>Thanks for your interest!</h4>
            {showProductsInModal ? (
              <DirectModalContent
                gym={gym}
                monthlyProduct={monthlyProduct}
                dailyProduct={dailyProduct}
                purchasable={purchasable}
              />
            ) : (
              <DefaultModalContent gym={gym} purchasable={purchasable} />
            )}
          </div>
        </Modal>
      </div>
    </div>
  );
};
